import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useDispatch, useSelector } from "react-redux";
import Toasts from "../../../core/common/toast";
import dayjs from "dayjs";
import { findAllEmployeesAttendance } from "../../../core/data/redux/reportingEmployeesSlice";
import { employeesAttendanceUpdate, getAttendanceDocument } from "../../../core/data/redux/employeesAttendanceSlice";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import AttendanceDetailsModel from "../../../core/modals/attendanceDetailsModel";
import { Avatar, Modal, Button } from "antd";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = Yup.object().shape({
  reason: Yup.string().required("Reason is required"),
  attendance_status: Yup.string(),
  half_day_type: Yup.string(),
});

const ReportingEmployeesDailyAttendence = () => {
  const reportingEmployeesState = useSelector(
    (state: any) => state.reportingEmployeesState
  );
  const employeesAttendanceState = useSelector(
    (state: any) => state.employeesAttendanceState
  );

  const [currentItem, setCurrentItem] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [approveModalItem, setApproveModalItem] = useState<any>(null);
  const [actionType, setActionType] = useState<any>(null);
  const [dayType, setDayType] = useState<string>("");
  const [locationValue, setLocationValue] = useState<boolean>(true);
  const [earlyLeavingValue, setEarlyLeavingValue] = useState<boolean>(true);
  const [lateComingValue, setLateComingValue] = useState<boolean>(true);
  const [docurl, setDocurl] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format("MMMM"));
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().format("YYYY"));

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const dispatch = useDispatch();

  const [dataList, setDataList] = useState<any[]>([]);
  const routes = all_routes;
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const showLocation = (record: any) => {
    setCurrentItem(record);
    setShowModal(true);
  }

  const showApproveModal = (record: any) => {
    setApproveModalItem(record);
    setActionType(null);
    setDayType("");
    setLocationValue(true);
    setEarlyLeavingValue(true);
    setLateComingValue(true);
    setValue("reason", record?.reason || "");
    setValue("attendance_status", record?.attendance_status || "");
    setValue("half_day_type", record?.half_day_type || "");
    dispatch(getAttendanceDocument({ id: record.id }));
  }

  React.useEffect(() => {
    if (approveModalItem) {
      setValue("reason", approveModalItem?.reason || "");
      setValue("attendance_status", approveModalItem?.attendance_status || "");
      setValue("half_day_type", approveModalItem?.half_day_type || "");
      setDayType("");
      setLocationValue(true);
      setEarlyLeavingValue(true);
      setLateComingValue(true);
    }
  }, [approveModalItem]);

  React.useEffect(() => {
    if (
      employeesAttendanceState.loading === "fulfilled" &&
      employeesAttendanceState.action === "get"
    ) {
      const result = employeesAttendanceState?.data?.data || null;
      setDocurl(result.url);
    }
  }, [employeesAttendanceState.loading]);

  const onSubmit = async (data: any, event: any) => {
    if (!actionType) {
      setError("reason", {
        type: "manual",
        message: "Please select an action type.",
      });
      return;
    }

    if (!data?.reason) {
      setError("reason", {
        type: "manual",
        message: "Reason is required.",
      });
      return;
    }

    const isVerified = "VERIFIED"; // Only accept action now

    const payload: any = {
      id: approveModalItem.id,
      is_verified: isVerified,
      reason: data?.reason ? data.reason : '',
      attendance_status: 'PRESENT',
    };

    // Only include these fields if user has selected them (checkbox is checked)
    // Checked means !locationValue === true, so locationValue === false
    // If checkbox is checked, include the key with value true
    if (approveModalItem?.is_location_mismatch === true && !locationValue) {
      payload.location_mismatch = true;
    }
    if (approveModalItem?.is_early_leaving === true && !earlyLeavingValue) {
      payload.early_leaving = true;
    }
    if (approveModalItem?.is_late_coming === true && !lateComingValue) {
      payload.late_coming = true;
    }

    await dispatch(employeesAttendanceUpdate(payload));
    setApproveModalItem(null);
    reset();
    setActionType(null);
    setDayType("");
    setLocationValue(true);
    setEarlyLeavingValue(true);
    setLateComingValue(true);
    fetchRecords(pagination, query);
  }

  const halfDayTypeOptions = {
    FIRST_HALF: "First Half",
    SECOND_HALF: "Second Half",
    NONE: "None",
  };

  // Debounce function
  function debounce(func: (...args: any[]) => void, delay: number) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  const fetchRecords = (
    pagination = { current: 1, pageSize: 10 },
    empId: string = ""
  ) => {
    dispatch(
      findAllEmployeesAttendance({
        page: pagination.current,
        limit: pagination.pageSize,
        empId: empId,
      })
    );
  };

  const randomAvatar = (firstName: string) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#d46b08",
    ];
    const index = Math.floor(Math.random() * colors.length);
    const initial = firstName?.charAt(0)?.toUpperCase() || "?";
    return {
      bg: colors[index],
      initial,
    };
  };

  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      if (trimmed === "") {
        setQuery("");
        fetchRecords(pagination, ""); // full data
        return;
      }
      if (trimmed.length < 4) return; // wait for at least 4 digits
      setQuery(trimmed);
      fetchRecords(pagination, trimmed);
    }, 500)
  ).current;

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchRecords(pagination, query);
  }, []);

  useEffect(() => {
    if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "assign"
    ) {
      fetchRecords(pagination, query);
    } else if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "list"
    ) {
      const result = reportingEmployeesState?.data?.data || null;
      if (result?.data && result?.data?.length > 0) {
        const employees = result?.data;
        setDataList(employees);
        setPagination({
          ...pagination,
          total: result.total || employees.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [reportingEmployeesState.loading]);

  const attendanceColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (text: string, record: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },

    {
      title: "Name",
      dataIndex: "employee",
      render: (text: string, record: any) => {
        const first = record?.employee?.details?.firstname?.trim() || "";
        const last =
          record?.employee?.details?.lsastname?.trim() || record?.employee?.details?.lastname?.trim() || "";

        if (!first && !last) {
          return <div style={{ height: "40px" }}></div>;
        }

        const avatarInfo = randomAvatar(first);

        return (
          <div className="d-flex align-items-center">
            {record?.employee?.sap_employee_images?.image ?
              <Avatar
                src={`data:image/jpeg;base64,${record?.employee?.sap_employee_images?.image}`}
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: '50px',
                  height: '50px'
                }}
              ></Avatar>
              :
              <Avatar
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: '50px',
                  height: '50px'
                }}
              >
                {avatarInfo.initial}
              </Avatar>
            }
            <div>{`${first} ${last}`.trim()}</div>
          </div>
        );
      },
    },
    {
      title: "Employee Name",
      dataIndex: "employee",
      //sorter: (a: any, b: any) => a.employee.first_name.length - b.employee.first_name.length,
      render: (employee: any) => `${employee?.details?.firstname}` || '',
    },

    {
      title: "Emp ID",
      dataIndex: "empId",
      render: (empId: string) => parseInt(empId, 10),
    },

    {
      title: "Check In Location",
      dataIndex: "check_in_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link to={"#"} className="text-decoration-none" onClick={() => showLocation(record)}>
            <div><b>Latitude</b>: {record.check_in_latitude}</div>
            <div><b>Longitude</b>: {record.check_in_longitude}</div>
          </Link>
        </div>
      )
    },
    {
      title: "Check In",
      dataIndex: "check_in_time",
      render: (text: string, record: any) =>
        text ? dayjs(record.check_in_time).format("DD-MM-YYYY hh:mm A") : "-",
    },



    {
      title: "Check Out",
      dataIndex: "check_out_time",
      render: (text: string, record: any) =>
        text
          ? dayjs(record.check_out_time).format("DD-MM-YYYY hh:mm A")
          : "-",
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      render: (text: string) => text || "-",
    },

    {
      title: "Wing",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name ? `${employee?.details?.sap_department_designation_wings?.wing_name}` : "",
    },
    {
      title: "Department",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name ? `${employee?.details?.sap_department_designation_wings?.department_name}` : "",
    },
    {
      title: "Designation",
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.designation_name ? `${employee?.details?.sap_department_designation_wings?.designation_name}` : "",
    },
    {
      title: "Attendance Status",
      dataIndex: "attendance_status",
      render: (text: string, record: any) => {
        const hasCheckIn = record.check_in_time && record.check_in_time !== null;
        const hasCheckOut = record.check_out_time && record.check_out_time !== null;
        const hasBoth = hasCheckIn && hasCheckOut;

        return (
          <div className="d-flex align-items-center gap-2">
            {/* <span
              style={{
                backgroundColor: getStatusColor(text),
                color: "#fff",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              {text.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span> */}
            {hasBoth && (
              <Button
                type="primary"
                size="small"
                onClick={() => showApproveModal(record)}
              >
                Regularize
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Verification",
      dataIndex: "is_verified",
    },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PRESENT: "#049123",
      ABSENT: "#C0282C",
      HALF_DAY: "#FFA500",
      LATE_COMING: "#B09BFB",
      EARLY_LEAVING: "#FFFF00",
      HOLIDAY: "#969696",
    };
    return colors[status] || "#ccc";
  };

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords(pagination, query);
  };

  return (
    <div>
      <>
        <Toasts
          show={reportingEmployeesState.loading === "rejected"}
          message={reportingEmployeesState.error}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={
            reportingEmployeesState.loading === "fulfilled" &&
            reportingEmployeesState.action !== "list"
          }
          message={reportingEmployeesState?.data?.message}
          title={"Success"}
          type={"success"}
        />
        <Toasts
          show={employeesAttendanceState.loading === "rejected"}
          message={employeesAttendanceState.error}
          title={"Failed"}
          type={"danger"}
        />
        <Toasts
          show={
            employeesAttendanceState.loading === "fulfilled" &&
            employeesAttendanceState.action === "update"
          }
          message={employeesAttendanceState?.data?.message}
          title={"Success"}
          type={"success"}
        />
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Employees Attendance</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.adminDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Employees</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Employees Attendance
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            {/* Dashboard Section */}
            <div className="row mb-3">
              <div className="col-md-12">
                <div className="d-flex gap-2 mb-3">
                  <div className="flex-fill">
                    <select
                      className="form-control form-control-sm"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      style={{
                        borderRadius: "6px",
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        fontSize: "13px"
                      }}
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const month = dayjs().month(i).format("MMMM");
                        return (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="flex-fill">
                    <select
                      className="form-control form-control-sm"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      style={{
                        borderRadius: "6px",
                        padding: "6px 12px",
                        border: "1px solid #ddd",
                        fontSize: "13px"
                      }}
                    >
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = (dayjs().year() - 5 + i).toString();
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Attendance Card */}

            {/* Summary Cards */}
            <div className="row mb-4">
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: "13px", fontWeight: "500" }}>Presence</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#333" }}>0</h3>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "#E8F5E9", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-user-check fs-20" style={{ color: "#049123" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: "13px", fontWeight: "500" }}>Absence</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#333" }}>0</h3>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "#FFEBEE", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-user-x fs-20" style={{ color: "#C0282C" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: "13px", fontWeight: "500" }}>Half Day</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#333" }}>0</h3>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "#F5F5F5", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-clock-hour-4 fs-20" style={{ color: "#666" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: "13px", fontWeight: "500" }}>Late Coming</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#333" }}>0</h3>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "#F3E5F5", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-clock-exclamation fs-20" style={{ color: "#B09BFB" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: "13px", fontWeight: "500" }}>Early Leaving</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#333" }}>0</h3>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "#FFF9C4", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-clock-minus fs-20" style={{ color: "#F57C00" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2 col-sm-6 mb-3">
                <div className="card border-0 shadow-sm" style={{ borderRadius: "8px", backgroundColor: "#9E9E9E" }}>
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-2" style={{ fontSize: "13px", fontWeight: "500", color: "#fff" }}>Holiday</h6>
                        <h3 className="mb-0" style={{ fontSize: "24px", fontWeight: "600", color: "#fff" }}>0</h3>
                        <small style={{ fontSize: "11px", color: "rgba(255,255,255,0.8)" }}>Days</small>
                      </div>
                      <div className="avatar avatar-md rounded-circle" style={{ backgroundColor: "rgba(255,255,255,0.2)", width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="ti ti-calendar-event fs-20" style={{ color: "#fff" }}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                <h4 className="mb-4 mt-3">Employees Attendance</h4>
              </div>
              <div className="card-body p-0 py-3">
                <Table
                  columns={attendanceColumns}
                  dataSource={dataList}
                  Selection={false}
                  paginationConfig={pagination}
                  handlePagination={handlePagination}
                  loading={reportingEmployeesState.loading === "pending"}
                  handleSearch={handleSearch}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <div className="col-md-12">
                <div className="datatable-paginate mt-4" />
              </div>
            </div>
          </div>
        </div>
      </>
      {currentItem && <AttendanceDetailsModel show={showModal} item={currentItem} close={() => {
        setShowModal(false);
        setCurrentItem(null);
      }} />
      }

      <Modal
        title={"Approve Action"}
        open={!!approveModalItem}
        onCancel={() => {
          setApproveModalItem(null);
          reset();
          setActionType(null);
          setDayType("");
          setLocationValue(true);
          setEarlyLeavingValue(true);
          setLateComingValue(true);
        }}
        footer={false}
        width={600}
      >
        <div className="row">
          <div className="col-md-6">
            {docurl && <img src={docurl} width={400} height={400} alt="Document" />}
          </div>
          <div className="col-md-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label style={{ float: "left" }}>Action Type</label>
                  <select
                    className="form-control"
                    value={actionType || ""}
                    disabled={approveModalItem?.is_verified !== "PENDING"}
                    onChange={(e) => {
                      setActionType(e.target.value);
                      setValue('attendance_status', 'PRESENT');
                    }}
                  >
                    <option value="">Select Option</option>
                    <option key={'accept'} value={'accept'}>Accept</option>
                    {/* <option key={'reject'} value={'reject'}>Reject</option> */}
                  </select>
                </div>

                {/* Checkboxes for Accept Action - Only show if the corresponding field is true */}
                {actionType === 'accept' && (
                  <div className="col-md-12 mb-3">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{ float: "left", marginBottom: '8px' }}>Options</label>

                      {/* Show Location checkbox only if is_location_mismatch is true */}
                      {approveModalItem?.is_location_mismatch === true && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="location"
                            checked={!locationValue}
                            onChange={(e) => setLocationValue(!e.target.checked)}
                            disabled={approveModalItem?.is_verified !== "PENDING"}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label htmlFor="location" style={{ margin: 0, cursor: 'pointer' }}>Location</label>
                        </div>
                      )}

                      {/* Show Early Leaving checkbox only if is_early_leaving is true */}
                      {approveModalItem?.is_early_leaving === true && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="earlyLeaving"
                            checked={!earlyLeavingValue}
                            onChange={(e) => setEarlyLeavingValue(!e.target.checked)}
                            disabled={approveModalItem?.is_verified !== "PENDING"}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label htmlFor="earlyLeaving" style={{ margin: 0, cursor: 'pointer' }}>Early Leaving</label>
                        </div>
                      )}

                      {/* Show Late Coming checkbox only if is_late_coming is true */}
                      {approveModalItem?.is_late_coming === true && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            id="lateComing"
                            checked={!lateComingValue}
                            onChange={(e) => setLateComingValue(!e.target.checked)}
                            disabled={approveModalItem?.is_verified !== "PENDING"}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label htmlFor="lateComing" style={{ margin: 0, cursor: 'pointer' }}>Late Coming</label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Day Type Dropdown for Reject Action - Commented out */}
                {/* {actionType === 'reject' && (
                  <div className="col-md-12 mb-3">
                    <label style={{ float: "left" }}>Day Type</label>
                    <select
                      className="form-control"
                      value={dayType}
                      disabled={approveModalItem?.is_verified !== "PENDING"}
                      onChange={(e) => {
                        setDayType(e.target.value);
                        if (e.target.value === 'FULL_DAY') {
                          setValue('half_day_type', '');
                        }
                      }}
                    >
                      <option value="">Select Option</option>
                      <option value="FULL_DAY">Full Day</option>
                      <option value="HALF_DAY">Half Day</option>
                    </select>
                  </div>
                )} */}

                {/* Half Day Type Dropdown - Only shown when Reject + Half Day - Commented out */}
                {/* {actionType === 'reject' && dayType === 'HALF_DAY' && (
                  <div className="col-md-12 mb-3">
                    <label style={{ float: "left" }}>Half Day Type</label>
                    <select
                      className="form-control"
                      {...register("half_day_type")}
                      disabled={approveModalItem?.is_verified !== "PENDING"}
                    >
                      <option value="">Select Option</option>
                      {Object.entries(halfDayTypeOptions).filter(([key]) => key !== 'NONE').map(
                        ([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        )
                      )}
                    </select>
                    {errors.half_day_type && (
                      <small className="text-danger" style={{ float: "left" }}>
                        {errors.half_day_type.message}
                      </small>
                    )}
                  </div>
                )} */}

                {/* Reason Section */}
                <div className="col-md-12 mb-3">
                  <label style={{ float: "left" }}>
                    Remarks <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Remarks"
                    rows={4}
                    {...register("reason")}
                    disabled={approveModalItem?.is_verified !== "PENDING"}
                  />
                  {errors.reason && (
                    <small className="text-danger" style={{ float: "left" }}>
                      {errors.reason.message}
                    </small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div
                    className="modal-footer"
                    style={{ gap: 20, border: "none" }}
                  >
                    {approveModalItem?.is_verified === "PENDING" ? (
                      <div className="d-flex justify-content-center">
                        <button
                          type="submit"
                          className="btn btn-primary px-4 py-2"
                          style={{ fontSize: "16px", fontWeight: "500" }}
                        >
                          Submit
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled
                      >
                        {approveModalItem?.is_verified}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReportingEmployeesDailyAttendence;
